import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  CancelOutlined as CancelIcon,
  Download as DownloadIcon,
  PlayArrow as StartIcon,
  Refresh as RetryIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';

import conversationService, {
  BlobDownload,
  ConversationDealReconciliationJob,
} from '../../services/conversationService';
import projectService, { ProjectMetadata } from '../../services/projectService';

interface Props {
  open: boolean;
  onClose: () => void;
  onCompleted?: () => void;
}

const ACTIVE = new Set(['queued', 'running']);
const TERMINAL = new Set(['completed', 'completed_with_errors', 'cancelled']);

const errorMessage = (error: unknown): string =>
  String((error as any)?.response?.data?.detail || (error as any)?.message || 'No fue posible completar la operación.');

const downloadBlob = ({ blob, filename }: BlobDownload) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename || 'archivo.xlsx';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

const statusColor = (status: string): 'default' | 'success' | 'warning' | 'error' | 'info' => {
  if (status === 'succeeded') return 'success';
  if (status === 'partial' || status === 'skipped') return 'warning';
  if (status === 'failed') return 'error';
  if (status === 'processing') return 'info';
  return 'default';
};

const BulkDealReconciliationDialog: React.FC<Props> = ({ open, onClose, onCompleted }) => {
  const [project, setProject] = useState<ProjectMetadata | null>(null);
  const [projects, setProjects] = useState<ProjectMetadata[]>([]);
  const [projectSearch, setProjectSearch] = useState('');
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [job, setJob] = useState<ConversationDealReconciliationJob | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const active = Boolean(job && ACTIVE.has(job.status));
  const terminal = Boolean(job && TERMINAL.has(job.status));
  const previewRows = useMemo(() => (job?.rows || []).slice(0, 100), [job?.rows]);

  useEffect(() => {
    if (!open) return undefined;
    let mounted = true;
    setProjectsLoading(true);
    projectService.getProjects(0, 100, true, projectSearch.trim() || undefined)
      .then((response) => mounted && setProjects(response.projects || []))
      .catch((loadError) => mounted && setError(errorMessage(loadError)))
      .finally(() => mounted && setProjectsLoading(false));
    return () => { mounted = false; };
  }, [open, projectSearch]);

  useEffect(() => {
    if (!open || !job?.job_id || !ACTIVE.has(job.status)) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const next = await conversationService.getConversationDealReconciliation(job.job_id);
        if (cancelled) return;
        if (TERMINAL.has(next.status)) {
          const rows = await conversationService.getConversationDealReconciliationRows(next.job_id, 100);
          next.rows = rows.rows;
          onCompleted?.();
        }
        setJob((previous) => ({ ...next, rows: next.rows.length ? next.rows : previous?.rows || [] }));
      } catch (pollError) {
        if (!cancelled) setError(errorMessage(pollError));
      }
    };
    const timer = window.setInterval(poll, 2500);
    void poll();
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [job?.job_id, job?.status, onCompleted, open]);

  const run = async (operation: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try { await operation(); } catch (operationError) { setError(errorMessage(operationError)); }
    finally { setBusy(false); }
  };

  const handleFile = (event: ChangeEvent<HTMLInputElement>) => {
    setFile(event.target.files?.[0] || null);
    setJob(null);
    setError(null);
  };

  const close = () => {
    onClose();
    if (!active) {
      setProject(null);
      setFile(null);
      setJob(null);
      setError(null);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="lg" fullWidth>
      <DialogTitle>Reconciliar proyecto y deal de conversaciones</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Alert severity="warning">
            Esta operación reemplaza el proyecto y el deal de la conversación activa más reciente.
            Cada deal se valida en vivo contra el proyecto seleccionado antes de cambiar datos y las
            etapas comerciales avanzadas quedan protegidas.
          </Alert>

          <Autocomplete
            options={projects}
            value={project}
            loading={projectsLoading}
            disabled={active || busy}
            getOptionLabel={(option) => option.project_name || option.project_code}
            isOptionEqualToValue={(option, selected) => option.project_code === selected.project_code}
            filterOptions={(options) => options}
            onInputChange={(_, value) => setProjectSearch(value)}
            onChange={(_, value) => { setProject(value); setJob(null); }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Proyecto canónico"
                required
                helperText={project ? `Se almacenará ${project.project_code}` : 'Aplica a todas las filas.'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: <>{projectsLoading && <CircularProgress size={20} />}{params.InputProps.endAdornment}</>,
                }}
              />
            )}
          />

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Usa únicamente las columnas <code>phone_number</code> y <code>hubspot_deal_id</code>.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                disabled={busy}
                onClick={() => run(async () => downloadBlob(await conversationService.downloadConversationDealReconciliationTemplate()))}
              >
                Descargar plantilla
              </Button>
              <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={active || busy}>
                {file ? 'Cambiar archivo' : 'Seleccionar .xlsx'}
                <input hidden type="file" accept=".xlsx" onChange={handleFile} />
              </Button>
              <Button
                variant="contained"
                disabled={!project || !file || active || busy}
                onClick={() => run(async () => {
                  if (project && file) setJob(await conversationService.validateConversationDealReconciliation(project.project_code, file));
                })}
              >
                Validar
              </Button>
            </Stack>
            {file && <Typography variant="caption">Archivo: {file.name}</Typography>}
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
          {job && (
            <>
              <Alert severity={job.invalid_rows ? 'warning' : 'success'}>
                Proyecto: {job.project_name} ({job.project_code}). Válidas: {job.valid_rows}; inválidas: {job.invalid_rows}.
              </Alert>
              <Box>
                <LinearProgress variant="determinate" value={job.progress_percent} />
                <Typography variant="caption">
                  {job.processed_rows}/{job.total_rows} · {job.progress_percent.toFixed(1)}%
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label={`Completadas ${job.succeeded_rows}`} color="success" />
                <Chip label={`Parciales ${job.partial_rows}`} color="warning" />
                <Chip label={`Fallidas ${job.failed_rows}`} color="error" />
                <Chip label={`Omitidas ${job.skipped_rows}`} />
              </Stack>
              <TableContainer sx={{ maxHeight: 360 }}>
                <Table size="small" stickyHeader>
                  <TableHead><TableRow>
                    <TableCell>Fila</TableCell><TableCell>Teléfono</TableCell><TableCell>Deal</TableCell>
                    <TableCell>Conversación</TableCell><TableCell>Etapa</TableCell><TableCell>Resultado</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {previewRows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.row_number}</TableCell>
                        <TableCell>{row.phone_number || '—'}</TableCell>
                        <TableCell>{row.hubspot_deal_id || '—'}</TableCell>
                        <TableCell>{row.conversation_id || '—'}</TableCell>
                        <TableCell>{row.stage_decision || '—'}</TableCell>
                        <TableCell>
                          <Chip size="small" color={statusColor(row.status)} label={row.status} />
                          {(row.error_message || row.warning_message) && (
                            <Typography variant="caption" display="block">{row.error_message || row.warning_message}</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {job?.status === 'validated' && (
          <Button startIcon={<StartIcon />} variant="contained" disabled={busy || !job.valid_rows}
            onClick={() => run(async () => {
              const queued = await conversationService.queueConversationDealReconciliation(job.job_id);
              queued.rows = job.rows;
              setJob(queued);
            })}>
            Ejecutar
          </Button>
        )}
        {active && (
          <Button startIcon={<CancelIcon />} color="warning" disabled={busy}
            onClick={() => run(async () => {
              const cancelled = await conversationService.cancelConversationDealReconciliation(job!.job_id);
              const rows = await conversationService.getConversationDealReconciliationRows(cancelled.job_id, 100);
              cancelled.rows = rows.rows;
              setJob(cancelled);
            })}>
            Cancelar pendientes
          </Button>
        )}
        {terminal && job && (
          <>
            {(job.failed_rows > 0 || job.partial_rows > 0) && (
              <Button startIcon={<RetryIcon />} disabled={busy}
                onClick={() => run(async () => {
                  const retried = await conversationService.retryConversationDealReconciliation(job.job_id);
                  retried.rows = job.rows;
                  setJob(retried);
                })}>
                Reintentar fallidas
              </Button>
            )}
            <Button startIcon={<DownloadIcon />} disabled={busy}
              onClick={() => run(async () => downloadBlob(await conversationService.downloadConversationDealReconciliationResults(job.job_id)))}>
              Descargar resultados
            </Button>
          </>
        )}
        <Button onClick={close} disabled={busy}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkDealReconciliationDialog;
